import { useState, useMemo, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import {
  Home,
  LayoutGrid,
  UploadCloud,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Minus,
  Search,
  CheckCircle2,
  AlertTriangle,
  CircleAlert,
  Info,
  Building2,
  Download,
  Trash2,
  Upload,
  Link2,
  Loader2,
  RefreshCw,
  KeyRound,
  LogIn,
  LogOut,
  Users,
  UserPlus,
} from "lucide-react";

// ============================================================================
// CONFIGURACIÓN Y CONSTANTES
// ============================================================================

const NOMBRE_INSTITUCION = "FIPROTUY";
const NOMBRE_INSTITUCION_LARGO =
  "Fideicomiso para la Promoción y Fomento al Desarrollo Turístico y Económico del Estado de Yucatán";

// El frontend habla con el backend por rutas relativas: funciona igual si se sirve desde
// el propio servidor Express (recomendado) o desde otro origen configurado con CORS.
const API_BASE = "/api";

// Paleta institucional inspirada en la identidad gubernamental de Yucatán (azul/turquesa
// institucional + blanco). Se referencia mediante clases utilitarias base de Tailwind.
const SEMAFORO_CONFIG = {
  verde: {
    punto: "bg-green-500",
    texto: "text-green-700",
    fondoSuave: "bg-green-50",
    borde: "border-green-200",
    etiqueta: "En meta",
    Icono: CheckCircle2,
  },
  amarillo: {
    punto: "bg-amber-500",
    texto: "text-amber-700",
    fondoSuave: "bg-amber-50",
    borde: "border-amber-200",
    etiqueta: "En riesgo",
    Icono: AlertTriangle,
  },
  rojo: {
    punto: "bg-red-500",
    texto: "text-red-700",
    fondoSuave: "bg-red-50",
    borde: "border-red-200",
    etiqueta: "Fuera de meta",
    Icono: CircleAlert,
  },
  sinDatos: {
    punto: "bg-gray-300",
    texto: "text-gray-500",
    fondoSuave: "bg-gray-50",
    borde: "border-gray-200",
    etiqueta: "Sin datos",
    Icono: Info,
  },
};

// ============================================================================
// FUNCIONES AUXILIARES (formato, cálculo de semáforo, variación, periodos)
// ============================================================================

/** Ordena las mediciones de un indicador de forma ascendente por periodo. */
function ordenarMediciones(mediciones) {
  return [...(mediciones || [])].sort((a, b) => a.periodo.localeCompare(b.periodo));
}

/** Formatea un valor numérico según la unidad del indicador, usando es-MX. */
function formatearValor(valor, unidad) {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return "N/D";
  const unidadTexto = (unidad || "").toLowerCase();
  if (unidadTexto === "%") {
    return `${new Intl.NumberFormat("es-MX", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(valor)}%`;
  }
  if (unidadTexto.includes("peso")) {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(valor);
  }
  if (unidadTexto.includes("día")) {
    return `${new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 }).format(valor)} días`;
  }
  // Conserva un decimal para unidades no enteras (p. ej. "Veces", "Noches"); los conteos enteros no lo necesitan.
  const numeroFormateado = new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: Number.isInteger(valor) ? 0 : 1,
  }).format(valor);
  return unidad ? `${numeroFormateado} ${unidad}` : numeroFormateado;
}

/** Convierte un periodo AAAA-MM, AAAA-TN o AAAA en una etiqueta legible en español. */
function formatearPeriodo(periodo) {
  if (!periodo) return "N/D";
  const coincidenciaMes = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (coincidenciaMes) {
    const [, anio, mes] = coincidenciaMes;
    const fecha = new Date(Number(anio), Number(mes) - 1, 1);
    const texto = new Intl.DateTimeFormat("es-MX", { month: "short", year: "numeric" }).format(fecha);
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }
  const coincidenciaTrimestre = /^(\d{4})-T([1-4])$/.exec(periodo);
  if (coincidenciaTrimestre) {
    const [, anio, trimestre] = coincidenciaTrimestre;
    return `T${trimestre} ${anio}`;
  }
  return periodo;
}

/**
 * Calcula el estado de semáforo comparando el valor contra meta y umbralAmarillo,
 * respetando el sentido del indicador (ascendente = más es mejor, descendente = menos es mejor).
 */
function calcularSemaforo(indicador, valor) {
  if (valor === null || valor === undefined || Number.isNaN(valor)) return "sinDatos";
  const { meta, umbralAmarillo, sentido } = indicador;
  if (sentido === "descendente") {
    if (valor <= meta) return "verde";
    if (valor <= umbralAmarillo) return "amarillo";
    return "rojo";
  }
  if (valor >= meta) return "verde";
  if (valor >= umbralAmarillo) return "amarillo";
  return "rojo";
}

/** Devuelve la última medición válida (con valor no nulo) de un indicador, ya ordenada. */
function obtenerUltimaMedicion(indicador) {
  const ordenadas = ordenarMediciones(indicador.mediciones);
  return ordenadas.length ? ordenadas[ordenadas.length - 1] : null;
}

/**
 * Calcula la variación porcentual entre el último periodo y el inmediato anterior.
 * Protege contra división entre cero y valores nulos (periodos faltantes o sin dato).
 */
function calcularVariacion(mediciones) {
  const ordenadas = ordenarMediciones(mediciones);
  if (ordenadas.length < 2) return null;
  const actual = ordenadas[ordenadas.length - 1];
  const anterior = ordenadas[ordenadas.length - 2];
  if (
    actual?.valor === null || actual?.valor === undefined ||
    anterior?.valor === null || anterior?.valor === undefined ||
    anterior.valor === 0
  ) {
    return null;
  }
  const cambio = ((actual.valor - anterior.valor) / Math.abs(anterior.valor)) * 100;
  return { cambio, subio: actual.valor > anterior.valor, igual: actual.valor === anterior.valor };
}

// ============================================================================
// CLIENTE DE LA API (autenticación, usuarios e indicadores persistidos en el backend)
// ============================================================================

/**
 * Envoltura de fetch: agrega la base de la API, envía/recibe la cookie de sesión
 * (credentials: "include") y normaliza los errores del servidor a un solo formato.
 */
async function apiFetch(ruta, opciones = {}) {
  const respuesta = await fetch(`${API_BASE}${ruta}`, {
    credentials: "include",
    headers: opciones.body ? { "Content-Type": "application/json" } : undefined,
    ...opciones,
  });
  let cuerpo = null;
  try {
    cuerpo = await respuesta.json();
  } catch {
    cuerpo = null;
  }
  if (!respuesta.ok) {
    const error = new Error((cuerpo && cuerpo.error) || `Error del servidor (${respuesta.status}).`);
    error.status = respuesta.status;
    throw error;
  }
  return cuerpo;
}

/** Genera el contenido CSV y dispara la descarga de las mediciones de un indicador. */
function exportarMedicionesCSV(indicador) {
  const encabezados = ["Periodo", "Valor", "Numerador", "Denominador", "Meta", "Nota"];
  const filas = ordenarMediciones(indicador.mediciones).map((m) => [
    m.periodo,
    m.valor ?? "",
    m.numerador ?? "",
    m.denominador ?? "",
    m.meta ?? "",
    (m.nota ?? "").replace(/"/g, '""'),
  ]);
  const contenido = [encabezados, ...filas]
    .map((fila) => fila.map((campo) => `"${campo}"`).join(","))
    .join("\n");
  const blob = new Blob([`﻿${contenido}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = `${indicador.id}_mediciones.csv`;
  document.body.appendChild(enlace);
  enlace.click();
  document.body.removeChild(enlace);
  URL.revokeObjectURL(url);
}

// ============================================================================
// MÓDULO DE CARGA DE DATOS (Fase 2): esquema, validación e importación de Excel
// ============================================================================

const NOMBRE_HOJA_INDICADORES = "Indicadores";
const NOMBRE_HOJA_MEDICIONES = "Mediciones";

const COLUMNAS_INDICADORES = [
  "id", "nombre", "categoria", "descripcion", "objetivo", "unidad", "sentido", "meta",
  "umbral_amarillo", "frecuencia", "fuente", "responsable", "formula", "numerador_desc",
  "denominador_desc", "criterios", "limitaciones", "notas", "tipo_grafica",
];
const COLUMNAS_OBLIGATORIAS_INDICADORES = ["id", "nombre", "categoria", "unidad", "sentido", "meta", "umbral_amarillo", "frecuencia"];

const COLUMNAS_MEDICIONES = ["id_indicador", "periodo", "valor", "numerador", "denominador", "meta", "nota"];
const COLUMNAS_OBLIGATORIAS_MEDICIONES = ["id_indicador", "periodo", "valor"];

/** Valida que un periodo tenga formato AAAA, AAAA-MM (01-12) o AAAA-TN (T1-T4). */
function periodoValido(periodo) {
  if (typeof periodo !== "string" || !periodo) return false;
  if (/^\d{4}$/.test(periodo)) return true;
  const mesCoincide = /^\d{4}-(\d{2})$/.exec(periodo);
  if (mesCoincide) {
    const mes = Number(mesCoincide[1]);
    return mes >= 1 && mes <= 12;
  }
  return /^\d{4}-T[1-4]$/.test(periodo);
}

/** Convierte una celda a número; null si está vacía, NaN si tiene contenido no numérico. */
function aNumeroONulo(valor) {
  if (valor === undefined || valor === null || String(valor).trim() === "") return null;
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : NaN;
}

/** Lee un archivo Excel/CSV como libro de SheetJS a partir de un objeto File del navegador. */
function leerLibroDesdeArchivo(archivo) {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = (evento) => {
      try {
        resolve(XLSX.read(new Uint8Array(evento.target.result), { type: "array" }));
      } catch (error) {
        reject(error);
      }
    };
    lector.onerror = () => reject(lector.error);
    lector.readAsArrayBuffer(archivo);
  });
}

/**
 * Valida el libro cargado contra el esquema esperado: hojas presentes, columnas
 * obligatorias, tipos de dato, formato de periodo y que cada id_indicador de Mediciones
 * exista en Indicadores. Si hay cualquier error, no se devuelven datos para importar
 * (todo o nada), junto con el reporte de errores hoja/fila/columna/motivo.
 */
function validarLibro(libro) {
  const errores = [];
  const sinDatos = { errores, indicadores: [], mediciones: [] };

  const nombreHojaIndicadores = libro.SheetNames.find((n) => n.trim().toLowerCase() === NOMBRE_HOJA_INDICADORES.toLowerCase());
  const nombreHojaMediciones = libro.SheetNames.find((n) => n.trim().toLowerCase() === NOMBRE_HOJA_MEDICIONES.toLowerCase());
  if (!nombreHojaIndicadores) errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: "-", columna: "-", motivo: `No se encontró la hoja "${NOMBRE_HOJA_INDICADORES}".` });
  if (!nombreHojaMediciones) errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: "-", columna: "-", motivo: `No se encontró la hoja "${NOMBRE_HOJA_MEDICIONES}".` });
  if (errores.length) return sinDatos;

  const filasIndicadores = XLSX.utils.sheet_to_json(libro.Sheets[nombreHojaIndicadores], { defval: "" });
  const filasMediciones = XLSX.utils.sheet_to_json(libro.Sheets[nombreHojaMediciones], { defval: "" });
  if (!filasIndicadores.length) errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: "-", columna: "-", motivo: "La hoja no contiene filas de datos." });
  if (!filasMediciones.length) errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: "-", columna: "-", motivo: "La hoja no contiene filas de datos." });
  if (errores.length) return sinDatos;

  const columnasIndicadores = Object.keys(filasIndicadores[0]);
  COLUMNAS_OBLIGATORIAS_INDICADORES.forEach((columna) => {
    if (!columnasIndicadores.includes(columna)) errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: 1, columna, motivo: "Columna obligatoria faltante." });
  });
  const columnasMediciones = Object.keys(filasMediciones[0]);
  COLUMNAS_OBLIGATORIAS_MEDICIONES.forEach((columna) => {
    if (!columnasMediciones.includes(columna)) errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: 1, columna, motivo: "Columna obligatoria faltante." });
  });
  if (errores.length) return sinDatos;

  const idsVistos = new Set();
  const indicadoresValidados = [];
  filasIndicadores.forEach((fila, indice) => {
    const numeroFila = indice + 2;
    const id = String(fila.id || "").trim();
    if (!id) {
      errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: numeroFila, columna: "id", motivo: "El id no puede estar vacío." });
      return;
    }
    if (idsVistos.has(id)) {
      errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: numeroFila, columna: "id", motivo: `El id "${id}" está duplicado en la hoja.` });
      return;
    }
    idsVistos.add(id);

    COLUMNAS_OBLIGATORIAS_INDICADORES.forEach((columna) => {
      if (columna !== "id" && String(fila[columna] ?? "").trim() === "") {
        errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: numeroFila, columna, motivo: "Valor obligatorio faltante." });
      }
    });

    const sentido = String(fila.sentido || "").trim().toLowerCase();
    if (sentido !== "ascendente" && sentido !== "descendente") {
      errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: numeroFila, columna: "sentido", motivo: 'Debe ser "ascendente" o "descendente".' });
    }
    const meta = aNumeroONulo(fila.meta);
    if (meta === null || Number.isNaN(meta)) errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: numeroFila, columna: "meta", motivo: "Debe ser un valor numérico." });
    const umbralAmarillo = aNumeroONulo(fila.umbral_amarillo);
    if (umbralAmarillo === null || Number.isNaN(umbralAmarillo)) errores.push({ hoja: NOMBRE_HOJA_INDICADORES, fila: numeroFila, columna: "umbral_amarillo", motivo: "Debe ser un valor numérico." });

    indicadoresValidados.push({
      id,
      nombre: String(fila.nombre || "").trim(),
      categoria: String(fila.categoria || "").trim(),
      descripcion: String(fila.descripcion || "").trim(),
      objetivo: String(fila.objetivo || "").trim(),
      unidad: String(fila.unidad || "").trim(),
      sentido: sentido === "descendente" ? "descendente" : "ascendente",
      meta: meta || 0,
      umbralAmarillo: umbralAmarillo || 0,
      frecuencia: String(fila.frecuencia || "").trim(),
      fuente: String(fila.fuente || "").trim(),
      responsable: String(fila.responsable || "").trim(),
      metodologia: {
        formulaTexto: String(fila.formula || "").trim(),
        numerador: String(fila.numerador_desc || "").trim(),
        denominador: String(fila.denominador_desc || "").trim(),
        variables: [],
        criteriosInclusion: String(fila.criterios || "").trim(),
        limitaciones: String(fila.limitaciones || "").trim(),
        notasInterpretacion: String(fila.notas || "").trim(),
      },
      tipoGrafica: ["linea", "barra", "area"].includes(fila.tipo_grafica) ? fila.tipo_grafica : "linea",
    });
  });

  const combinacionesVistas = new Set();
  const medicionesValidadas = [];
  filasMediciones.forEach((fila, indice) => {
    const numeroFila = indice + 2;
    const idIndicador = String(fila.id_indicador || "").trim();
    const periodo = String(fila.periodo || "").trim();

    if (!idIndicador) {
      errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "id_indicador", motivo: "No puede estar vacío." });
      return;
    }
    if (!idsVistos.has(idIndicador)) {
      errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "id_indicador", motivo: `El id "${idIndicador}" no existe en la hoja "${NOMBRE_HOJA_INDICADORES}".` });
      return;
    }
    if (!periodoValido(periodo)) {
      errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "periodo", motivo: "Formato esperado: AAAA, AAAA-MM o AAAA-TN." });
      return;
    }
    const combinacion = `${idIndicador}|${periodo}`;
    if (combinacionesVistas.has(combinacion)) {
      errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "periodo", motivo: `Medición duplicada para "${idIndicador}" en el periodo "${periodo}".` });
      return;
    }
    combinacionesVistas.add(combinacion);

    const valor = aNumeroONulo(fila.valor);
    if (valor === null || Number.isNaN(valor)) {
      errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "valor", motivo: "Debe ser un valor numérico." });
      return;
    }
    const numerador = aNumeroONulo(fila.numerador);
    if (Number.isNaN(numerador)) errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "numerador", motivo: "Debe ser numérico o estar vacío." });
    const denominador = aNumeroONulo(fila.denominador);
    if (Number.isNaN(denominador)) errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "denominador", motivo: "Debe ser numérico o estar vacío." });
    const metaMedicion = aNumeroONulo(fila.meta);
    if (Number.isNaN(metaMedicion)) errores.push({ hoja: NOMBRE_HOJA_MEDICIONES, fila: numeroFila, columna: "meta", motivo: "Debe ser numérico o estar vacío." });

    medicionesValidadas.push({
      idIndicador,
      periodo,
      valor,
      numerador: Number.isNaN(numerador) ? null : numerador,
      denominador: Number.isNaN(denominador) ? null : denominador,
      meta: Number.isNaN(metaMedicion) ? null : metaMedicion,
      nota: String(fila.nota || "").trim(),
    });
  });

  if (errores.length) return sinDatos;
  return { errores: [], indicadores: indicadoresValidados, mediciones: medicionesValidadas };
}

/** Combina las filas validadas de ambas hojas en objetos indicador completos (con sus mediciones anidadas). */
function construirIndicadoresDesdeImportacion(indicadoresValidados, medicionesValidadas) {
  return indicadoresValidados.map((indicador) => ({
    ...indicador,
    mediciones: medicionesValidadas
      .filter((m) => m.idIndicador === indicador.id)
      .map((m) => ({
        periodo: m.periodo,
        valor: m.valor,
        numerador: m.numerador,
        denominador: m.denominador,
        meta: m.meta ?? indicador.meta,
        nota: m.nota,
      })),
  }));
}

/** Calcula el resumen de vista previa (conteos y rango de periodos) antes de confirmar la importación. */
function calcularResumenImportacion(indicadoresImportados) {
  const todasMediciones = indicadoresImportados.flatMap((i) => i.mediciones);
  const periodos = todasMediciones.map((m) => m.periodo).sort();
  return {
    numIndicadores: indicadoresImportados.length,
    numMediciones: todasMediciones.length,
    periodoInicio: periodos.length ? periodos[0] : null,
    periodoFin: periodos.length ? periodos[periodos.length - 1] : null,
  };
}

/**
 * Aplica la importación al estado actual de indicadores según el modo elegido:
 * "reemplazar" sustituye todo; "agregar" hace upsert por id de indicador y, dentro de
 * cada uno, upsert de mediciones por periodo (conservando periodos no incluidos en el archivo).
 */
function aplicarImportacion(indicadoresActuales, indicadoresImportados, modo) {
  if (modo === "reemplazar") return indicadoresImportados;

  const mapa = new Map(indicadoresActuales.map((ind) => [ind.id, { ...ind, mediciones: [...ind.mediciones] }]));
  indicadoresImportados.forEach((importado) => {
    const existente = mapa.get(importado.id);
    const medicionesBase = existente ? [...existente.mediciones] : [];
    importado.mediciones.forEach((nuevaMedicion) => {
      const indiceExistente = medicionesBase.findIndex((m) => m.periodo === nuevaMedicion.periodo);
      if (indiceExistente >= 0) medicionesBase[indiceExistente] = nuevaMedicion;
      else medicionesBase.push(nuevaMedicion);
    });
    mapa.set(importado.id, { ...importado, mediciones: medicionesBase });
  });
  return Array.from(mapa.values());
}

/** Genera y descarga la plantilla Excel con ambas hojas, encabezados y 2 filas de ejemplo cada una. */
function descargarPlantillaExcel() {
  const filasIndicadoresEjemplo = [
    ["IND-01", "Ocupación hotelera promedio", "Turismo", "Mide el porcentaje de habitaciones ocupadas respecto al inventario disponible.", "Apoyar decisiones de promoción estacional.", "%", "ascendente", 65, 55, "Mensual", "Reportes de establecimientos de hospedaje", "Coordinación de Inteligencia de Mercados", "(Habitaciones ocupadas / Habitaciones disponibles) × 100", "Habitaciones ocupadas", "Habitaciones disponibles", "Establecimientos con reporte completo", "Depende de la autodeclaración de los prestadores", "Los meses de temporada alta muestran valores más altos", "barra"],
    ["IND-02", "Tiempo promedio de atención a solicitudes", "Procesos Administrativos", "Mide los días naturales entre recepción y resolución de una solicitud.", "Detectar rezagos operativos.", "días", "descendente", 10, 15, "Mensual", "Sistema interno de control de expedientes", "Dirección de Administración", "Suma de días ÷ Número de solicitudes concluidas", "Días acumulados", "Solicitudes concluidas", "Solicitudes concluidas en el periodo", "No distingue por complejidad de trámite", "Valores por encima del umbral indican rezago", "linea"],
  ];
  const filasMedicionesEjemplo = [
    ["IND-01", "2026-01", 62.5, 750, 1200, 65, ""],
    ["IND-01", "2026-02", 68.1, 817, 1200, 65, "Periodo de temporada alta"],
  ];

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, XLSX.utils.aoa_to_sheet([COLUMNAS_INDICADORES, ...filasIndicadoresEjemplo]), NOMBRE_HOJA_INDICADORES);
  XLSX.utils.book_append_sheet(libro, XLSX.utils.aoa_to_sheet([COLUMNAS_MEDICIONES, ...filasMedicionesEjemplo]), NOMBRE_HOJA_MEDICIONES);
  XLSX.writeFile(libro, "plantilla_indicadores_fiprotuy.xlsx");
}

/**
 * Descarga el texto de una URL pública (por ejemplo, una hoja de Google Sheets publicada
 * con "Archivo → Compartir → Publicar en la web" en formato CSV). Falla si la URL no es
 * accesible o si el origen no habilita CORS para peticiones desde el navegador.
 */
async function obtenerTextoCSV(url) {
  const respuesta = await fetch(url);
  if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
  return respuesta.text();
}

/**
 * Construye un libro sintético de dos hojas ("Indicadores" y "Mediciones") a partir de dos
 * textos CSV independientes, para poder reutilizar exactamente la misma validación que se
 * aplica a los archivos Excel de dos hojas.
 */
function construirLibroDesdeCSV(textoIndicadores, textoMediciones) {
  // raw: true evita que SheetJS interprete periodos como "2026-05" como fechas al parsear CSV.
  const libroIndicadores = XLSX.read(textoIndicadores, { type: "string", raw: true });
  const libroMediciones = XLSX.read(textoMediciones, { type: "string", raw: true });
  return {
    SheetNames: [NOMBRE_HOJA_INDICADORES, NOMBRE_HOJA_MEDICIONES],
    Sheets: {
      [NOMBRE_HOJA_INDICADORES]: libroIndicadores.Sheets[libroIndicadores.SheetNames[0]],
      [NOMBRE_HOJA_MEDICIONES]: libroMediciones.Sheets[libroMediciones.SheetNames[0]],
    },
  };
}

// ============================================================================
// COMPONENTE: Header
// ============================================================================

function Header() {
  // Intenta cargar el logotipo oficial (logo-sefotur.png, junto a index.html); si no existe, usa un ícono de reserva.
  const [logoNoDisponible, setLogoNoDisponible] = useState(false);
  return (
    <header className="bg-teal-800 text-white">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-lg bg-white p-1.5 flex items-center justify-center shrink-0 overflow-hidden">
          {logoNoDisponible ? (
            <Building2 className="w-7 h-7 text-teal-700" aria-hidden="true" />
          ) : (
            <img
              src="logo-sefotur.png"
              alt="Logotipo SEFOTUR"
              className="w-full h-full object-contain"
              onError={() => setLogoNoDisponible(true)}
            />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-teal-100">Tablero de indicadores</p>
          <h1 className="text-lg sm:text-xl font-semibold leading-tight truncate">{NOMBRE_INSTITUCION}</h1>
          <p className="text-xs sm:text-sm text-teal-100 truncate">{NOMBRE_INSTITUCION_LARGO}</p>
        </div>
      </div>
    </header>
  );
}

// ============================================================================
// COMPONENTE: Nav
// ============================================================================

function Nav({ vista, setVista }) {
  const items = [
    { id: "inicio", etiqueta: "Inicio", Icono: Home },
    { id: "menu", etiqueta: "Indicadores", Icono: LayoutGrid },
    { id: "datos", etiqueta: "Cargar datos", Icono: UploadCloud },
    { id: "usuarios", etiqueta: "Usuarios", Icono: Users },
  ];
  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 flex gap-1">
        {items.map(({ id, etiqueta, Icono }) => {
          const activo = vista === id || (id === "menu" && vista === "detalle");
          return (
            <button
              key={id}
              onClick={() => setVista(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 ${
                activo
                  ? "border-teal-700 text-teal-800"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <Icono className="w-4 h-4" aria-hidden="true" />
              {etiqueta}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ============================================================================
// COMPONENTE: Inicio
// ============================================================================

function Inicio({ indicadores, esDemostracion, sesion, onIrAMenu, onIrACarga, onBorrarDemo }) {
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState("");

  const manejarBorrar = async () => {
    setBorrando(true);
    setErrorBorrar("");
    try {
      await onBorrarDemo();
    } catch (err) {
      setErrorBorrar(err.message);
    } finally {
      setBorrando(false);
    }
  };

  const resumen = useMemo(() => {
    const conteo = { verde: 0, amarillo: 0, rojo: 0, sinDatos: 0 };
    let periodoMasReciente = null;
    indicadores.forEach((indicador) => {
      const ultima = obtenerUltimaMedicion(indicador);
      const estado = ultima ? calcularSemaforo(indicador, ultima.valor) : "sinDatos";
      conteo[estado] += 1;
      if (ultima && (!periodoMasReciente || ultima.periodo > periodoMasReciente)) {
        periodoMasReciente = ultima.periodo;
      }
    });
    return { conteo, periodoMasReciente };
  }, [indicadores]);

  const fechaActualizacion = resumen.periodoMasReciente
    ? new Intl.DateTimeFormat("es-MX", { month: "long", year: "numeric" }).format(
        new Date(
          Number(resumen.periodoMasReciente.split("-")[0]),
          Number(resumen.periodoMasReciente.split("-")[1] || 1) - 1,
          1
        )
      )
    : "Sin datos cargados";

  const categorias = [...new Set(indicadores.map((i) => i.categoria))];

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-2xl font-bold text-gray-900">Acerca del tablero</h2>
        <p className="text-gray-600 mt-1">
          Seguimiento del desempeño de campañas turísticas y de procesos administrativos de {NOMBRE_INSTITUCION}.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Última actualización de datos:{" "}
          <span className="font-medium text-gray-700 capitalize">{fechaActualizacion}</span>
        </p>
        {esDemostracion && indicadores.length > 0 && (
          <div className="mt-3 inline-flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-3 py-2">
            <span>Estos son datos de demostración, no datos oficiales.</span>
            {sesion ? (
              <button
                onClick={manejarBorrar}
                disabled={borrando}
                className="inline-flex items-center gap-1 text-amber-900 underline underline-offset-2 hover:text-amber-950 disabled:opacity-60"
              >
                {borrando ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                Borrar datos de demostración
              </button>
            ) : (
              <span className="text-amber-700 text-xs">(inicia sesión para borrarlos)</span>
            )}
            {errorBorrar && <span className="text-red-700 text-xs">{errorBorrar}</span>}
          </div>
        )}
      </section>

      {/* Resumen ejecutivo */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <TarjetaResumen etiqueta="Indicadores" valor={indicadores.length} />
        <TarjetaResumen etiqueta="En meta" valor={resumen.conteo.verde} colorTexto="text-green-700" />
        <TarjetaResumen etiqueta="En riesgo" valor={resumen.conteo.amarillo} colorTexto="text-amber-700" />
        <TarjetaResumen etiqueta="Fuera de meta" valor={resumen.conteo.rojo} colorTexto="text-red-700" />
        <TarjetaResumen
          etiqueta="Periodo más reciente"
          valor={resumen.periodoMasReciente ? formatearPeriodo(resumen.periodoMasReciente) : "N/D"}
          esTexto
        />
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <BloqueTexto titulo="¿Qué es este tablero?">
          Es una herramienta de consulta para dar seguimiento periódico al desempeño de las campañas de
          promoción turística financiadas por {NOMBRE_INSTITUCION} y a la eficiencia de sus procesos
          administrativos internos, a partir de indicadores con meta, semáforo y serie histórica.
        </BloqueTexto>
        <BloqueTexto titulo="¿Para qué sirve?">
          Apoya decisiones como la reasignación de presupuesto entre campañas, la renovación de estrategias
          de contenido, la priorización de mejoras en procesos de atención a solicitudes y el seguimiento de
          cumplimiento normativo interno frente al Comité Técnico del fideicomiso.
        </BloqueTexto>
        <BloqueTexto titulo="¿Qué contiene?">
          Incluye {indicadores.length} indicador{indicadores.length === 1 ? "" : "es"} agrupados en las
          categorías:{" "}
          {categorias.length ? categorias.map((c, idx) => (
            <span key={c}>
              <span className="font-medium">{c}</span>
              {idx < categorias.length - 1 ? ", " : "."}
            </span>
          )) : "aún no hay categorías (no hay indicadores cargados)."}
        </BloqueTexto>
        <BloqueTexto titulo="¿Cómo usarlo?">
          <ol className="list-decimal list-inside space-y-1">
            <li>Entra a "Indicadores" para ver el panorama general y el semáforo de cada uno.</li>
            <li>Da clic en una tarjeta para ver su detalle, metodología y serie histórica completa.</li>
            <li>Usa "Cargar datos" para actualizar el tablero con información oficial más reciente.</li>
          </ol>
        </BloqueTexto>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <BloqueTexto titulo="Periodicidad de actualización">
          Los indicadores de este tablero se actualizan con frecuencia mensual, conforme se cierra la
          información operativa de cada área responsable.
        </BloqueTexto>
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Glosario</h3>
          <dl className="text-sm text-gray-600 space-y-2">
            <div>
              <dt className="font-medium text-gray-700 inline">Meta: </dt>
              <dd className="inline">valor objetivo que el indicador debe alcanzar o mantener.</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700 inline">Semáforo: </dt>
              <dd className="inline">clasificación visual (verde/amarillo/rojo) del desempeño frente a la meta.</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700 inline">Línea base: </dt>
              <dd className="inline">valor de referencia inicial contra el cual se compara la evolución del indicador.</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700 inline">Numerador / denominador: </dt>
              <dd className="inline">componentes de la razón que forma al indicador cuando este se calcula como cociente.</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-700 inline">Periodo: </dt>
              <dd className="inline">unidad de tiempo (mes, trimestre o año) a la que corresponde cada medición.</dd>
            </div>
          </dl>
        </div>
      </section>

      <div>
        <button
          onClick={onIrAMenu}
          className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white font-medium px-5 py-2.5 rounded-md transition-colors"
        >
          <LayoutGrid className="w-4 h-4" aria-hidden="true" />
          Ver indicadores
        </button>
      </div>
    </div>
  );
}

function TarjetaResumen({ etiqueta, valor, colorTexto = "text-gray-900", esTexto = false }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500">{etiqueta}</p>
      <p className={`mt-1 font-bold ${colorTexto} ${esTexto ? "text-base capitalize" : "text-2xl"}`}>{valor}</p>
    </div>
  );
}

function BloqueTexto({ titulo, children }) {
  return (
    <div>
      <h3 className="font-semibold text-gray-800 mb-2">{titulo}</h3>
      <div className="text-sm text-gray-600 leading-relaxed">{children}</div>
    </div>
  );
}

// ============================================================================
// COMPONENTE: MenuIndicadores
// ============================================================================

function MenuIndicadores({ indicadores, esDemostracion, onSeleccionar, onIrACarga }) {
  const [busqueda, setBusqueda] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("todas");
  const [semaforoFiltro, setSemaforoFiltro] = useState("todos");

  const categorias = useMemo(() => [...new Set(indicadores.map((i) => i.categoria))], [indicadores]);

  const indicadoresFiltrados = useMemo(() => {
    return indicadores.filter((indicador) => {
      const coincideBusqueda = indicador.nombre.toLowerCase().includes(busqueda.toLowerCase());
      const coincideCategoria = categoriaFiltro === "todas" || indicador.categoria === categoriaFiltro;
      const ultima = obtenerUltimaMedicion(indicador);
      const estado = ultima ? calcularSemaforo(indicador, ultima.valor) : "sinDatos";
      const coincideSemaforo = semaforoFiltro === "todos" || estado === semaforoFiltro;
      return coincideBusqueda && coincideCategoria && coincideSemaforo;
    });
  }, [indicadores, busqueda, categoriaFiltro, semaforoFiltro]);

  if (indicadores.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center">
        <LayoutGrid className="w-10 h-10 text-gray-300 mx-auto mb-3" aria-hidden="true" />
        <h3 className="font-semibold text-gray-800">Aún no hay indicadores cargados</h3>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          Carga un archivo de datos para comenzar a ver el tablero, o restaura los datos de demostración.
        </p>
        <button
          onClick={onIrACarga}
          className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white font-medium px-4 py-2 rounded-md text-sm"
        >
          <UploadCloud className="w-4 h-4" aria-hidden="true" />
          Ir a cargar datos
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Indicadores</h2>
        {esDemostracion && (
          <p className="text-xs text-amber-700 mt-1">Mostrando datos de demostración, no datos oficiales.</p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar indicador..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-600"
          />
        </div>
        <select
          value={categoriaFiltro}
          onChange={(e) => setCategoriaFiltro(e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
        >
          <option value="todas">Todas las categorías</option>
          {categorias.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={semaforoFiltro}
          onChange={(e) => setSemaforoFiltro(e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white"
        >
          <option value="todos">Todos los semáforos</option>
          <option value="verde">En meta</option>
          <option value="amarillo">En riesgo</option>
          <option value="rojo">Fuera de meta</option>
        </select>
      </div>

      {indicadoresFiltrados.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          No hay indicadores que coincidan con la búsqueda o los filtros aplicados.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {indicadoresFiltrados.map((indicador) => (
            <TarjetaIndicador
              key={indicador.id}
              indicador={indicador}
              onClick={() => onSeleccionar(indicador.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE: TarjetaIndicador
// ============================================================================

function TarjetaIndicador({ indicador, onClick }) {
  const ultima = obtenerUltimaMedicion(indicador);
  const estado = ultima ? calcularSemaforo(indicador, ultima.valor) : "sinDatos";
  const config = SEMAFORO_CONFIG[estado];
  const variacion = calcularVariacion(indicador.mediciones);
  const datosSparkline = ordenarMediciones(indicador.mediciones).slice(-12);

  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-teal-300 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-gray-500">{indicador.categoria}</p>
          <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">{indicador.nombre}</h3>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${config.fondoSuave} ${config.texto} border ${config.borde}`}
        >
          <span className={`w-2 h-2 rounded-full ${config.punto}`} aria-hidden="true" />
          {config.etiqueta}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div>
          <p className="text-2xl font-bold text-gray-900">
            {ultima ? formatearValor(ultima.valor, indicador.unidad) : "N/D"}
          </p>
          <p className="text-xs text-gray-500">Meta: {formatearValor(indicador.meta, indicador.unidad)}</p>
        </div>
        <VariacionIndicador variacion={variacion} />
      </div>

      <div className="h-10 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={datosSparkline}>
            <Line
              type="monotone"
              dataKey="valor"
              stroke="#0f766e"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </button>
  );
}

function VariacionIndicador({ variacion }) {
  if (!variacion || variacion.igual) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Minus className="w-3.5 h-3.5" aria-hidden="true" />
        {variacion ? "Sin cambio" : "N/D"}
      </span>
    );
  }
  const Icono = variacion.subio ? ArrowUp : ArrowDown;
  const color = variacion.subio ? "text-green-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icono className="w-3.5 h-3.5" aria-hidden="true" />
      {new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 }).format(Math.abs(variacion.cambio))}%
    </span>
  );
}

// ============================================================================
// COMPONENTE: DetalleIndicador
// ============================================================================

function DetalleIndicador({ indicador, onVolver }) {
  const [pestana, setPestana] = useState("descripcion");
  const [rango, setRango] = useState(12);
  const [tipoGrafica, setTipoGrafica] = useState(indicador.tipoGrafica || "linea");

  const mediciones = useMemo(() => ordenarMediciones(indicador.mediciones), [indicador]);
  const ultima = mediciones.length ? mediciones[mediciones.length - 1] : null;
  const estado = ultima ? calcularSemaforo(indicador, ultima.valor) : "sinDatos";
  const config = SEMAFORO_CONFIG[estado];
  const variacion = calcularVariacion(indicador.mediciones);

  const datosGrafica = useMemo(() => {
    if (rango === "todos") return mediciones;
    return mediciones.slice(-rango);
  }, [mediciones, rango]);

  const ComponenteGrafica = { linea: LineChart, barra: BarChart, area: AreaChart }[tipoGrafica] || LineChart;

  return (
    <div className="space-y-6">
      <button
        onClick={onVolver}
        className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 font-medium"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        Volver al menú
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-xs text-gray-500">{indicador.categoria}</p>
        <h2 className="text-xl font-bold text-gray-900">{indicador.nombre}</h2>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500 text-xs">Último valor</p>
            <p className="font-semibold text-gray-900">{ultima ? formatearValor(ultima.valor, indicador.unidad) : "N/D"}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Meta</p>
            <p className="font-semibold text-gray-900">{formatearValor(indicador.meta, indicador.unidad)}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Variación</p>
            <VariacionIndicador variacion={variacion} />
          </div>
          <div>
            <p className="text-gray-500 text-xs">Semáforo</p>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.fondoSuave} ${config.texto} border ${config.borde}`}>
              <span className={`w-2 h-2 rounded-full ${config.punto}`} aria-hidden="true" />
              {config.etiqueta}
            </span>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Frecuencia</p>
            <p className="font-medium text-gray-800">{indicador.frecuencia}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs">Responsable</p>
            <p className="font-medium text-gray-800">{indicador.responsable}</p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-500 text-xs">Fuente</p>
            <p className="font-medium text-gray-800">{indicador.fuente}</p>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 flex gap-1">
        {[
          { id: "descripcion", etiqueta: "Descripción" },
          { id: "metodologia", etiqueta: "Metodología de cálculo" },
          { id: "mediciones", etiqueta: "Mediciones" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setPestana(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 ${
              pestana === tab.id ? "border-teal-700 text-teal-800" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {tab.etiqueta}
          </button>
        ))}
      </div>

      {pestana === "descripcion" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 text-sm text-gray-700 leading-relaxed">
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Descripción</h3>
            {indicador.descripcion.split("\n\n").map((parrafo, idx) => (
              <p key={idx} className="mb-2">{parrafo}</p>
            ))}
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 mb-1">Objetivo</h3>
            <p>{indicador.objetivo}</p>
          </div>
        </div>
      )}

      {pestana === "metodologia" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5 text-sm text-gray-700">
          <div className="bg-teal-50 border border-teal-200 rounded-md p-4 text-center">
            <p className="text-xs text-teal-700 uppercase tracking-wide mb-1">Fórmula</p>
            <p className="font-semibold text-teal-900">{indicador.metodologia.formulaTexto}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Numerador</h4>
              <p>{indicador.metodologia.numerador}</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-1">Denominador</h4>
              <p>{indicador.metodologia.denominador}</p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Variables</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Variable</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Descripción</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Fuente</th>
                  </tr>
                </thead>
                <tbody>
                  {indicador.metodologia.variables.map((v, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-800">{v.nombre}</td>
                      <td className="px-3 py-2 text-gray-600">{v.descripcion}</td>
                      <td className="px-3 py-2 text-gray-500">{v.fuente}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">Criterios de inclusión/exclusión</h4>
            <p>{indicador.metodologia.criteriosInclusion}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">Limitaciones</h4>
            <p>{indicador.metodologia.limitaciones}</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">Notas de interpretación</h4>
            <p>{indicador.metodologia.notasInterpretacion}</p>
          </div>
        </div>
      )}

      {pestana === "mediciones" && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-2">
              {[6, 12, 24, "todos"].map((opcion) => (
                <button
                  key={opcion}
                  onClick={() => setRango(opcion)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
                    rango === opcion
                      ? "bg-teal-700 text-white border-teal-700"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {opcion === "todos" ? "Todos" : `Últimos ${opcion}`}
                </button>
              ))}
            </div>
            <select
              value={tipoGrafica}
              onChange={(e) => setTipoGrafica(e.target.value)}
              className="border border-gray-300 rounded-md text-xs px-3 py-1.5 bg-white"
            >
              <option value="linea">Línea</option>
              <option value="barra">Barra</option>
              <option value="area">Área</option>
            </select>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComponenteGrafica data={datosGrafica} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="periodo" tickFormatter={formatearPeriodo} tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0, notation: "compact" }).format(v)}
                  width={55}
                />
                <Tooltip content={<TooltipMediciones unidad={indicador.unidad} />} />
                <ReferenceLine
                  y={indicador.meta}
                  stroke="#0f766e"
                  strokeDasharray="6 4"
                  label={{ value: "Meta", position: "insideTopRight", fill: "#0f766e", fontSize: 11 }}
                />
                {tipoGrafica === "linea" && (
                  <Line type="monotone" dataKey="valor" stroke="#0f766e" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
                )}
                {tipoGrafica === "barra" && <Bar dataKey="valor" fill="#0f766e" radius={[3, 3, 0, 0]} />}
                {tipoGrafica === "area" && (
                  <Area type="monotone" dataKey="valor" stroke="#0f766e" fill="#99f6e4" connectNulls={false} />
                )}
              </ComponenteGrafica>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-800 text-sm">Todos los periodos</h4>
            <button
              onClick={() => exportarMedicionesCSV(indicador)}
              className="inline-flex items-center gap-2 text-xs font-medium border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50"
            >
              <Download className="w-3.5 h-3.5" aria-hidden="true" />
              Exportar CSV
            </button>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto border border-gray-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Periodo</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Valor</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Numerador</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Denominador</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Meta</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Nota</th>
                </tr>
              </thead>
              <tbody>
                {mediciones.map((m) => (
                  <tr key={m.periodo} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-800">{formatearPeriodo(m.periodo)}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{formatearValor(m.valor, indicador.unidad)}</td>
                    <td className="px-3 py-2 text-gray-500">{m.numerador ?? "N/D"}</td>
                    <td className="px-3 py-2 text-gray-500">{m.denominador ?? "N/D"}</td>
                    <td className="px-3 py-2 text-gray-500">{formatearValor(m.meta, indicador.unidad)}</td>
                    <td className="px-3 py-2 text-gray-500">{m.nota || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TooltipMediciones({ active, payload, unidad }) {
  if (!active || !payload || !payload.length) return null;
  const dato = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-md p-3 text-xs space-y-1">
      <p className="font-semibold text-gray-800">{formatearPeriodo(dato.periodo)}</p>
      <p className="text-gray-600">Valor: <span className="font-medium text-gray-900">{formatearValor(dato.valor, unidad)}</span></p>
      {dato.numerador !== null && dato.numerador !== undefined && (
        <p className="text-gray-500">Numerador: {dato.numerador}</p>
      )}
      {dato.denominador !== null && dato.denominador !== undefined && (
        <p className="text-gray-500">Denominador: {dato.denominador}</p>
      )}
      {dato.nota && <p className="text-amber-700">{dato.nota}</p>}
    </div>
  );
}

// ============================================================================
// COMPONENTE: CargaDatos
// ============================================================================

function CargaDatos({ indicadoresActuales, onImportar, onIrAMenu, sesion, onCerrarSesion }) {
  const [arrastrando, setArrastrando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [origen, setOrigen] = useState("");
  const [errores, setErrores] = useState([]);
  const [errorGeneral, setErrorGeneral] = useState("");
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [importado, setImportado] = useState(false);
  const [urlIndicadores, setUrlIndicadores] = useState("");
  const [urlMediciones, setUrlMediciones] = useState("");
  const [guardando, setGuardando] = useState(false);
  const inputArchivoRef = useRef(null);

  const limpiarResultadoAnterior = () => {
    setErrorGeneral("");
    setErrores([]);
    setVistaPrevia(null);
    setImportado(false);
  };

  const procesarResultadoValidacion = (resultado) => {
    if (resultado.errores.length) {
      setErrores(resultado.errores);
      return;
    }
    const indicadoresImportados = construirIndicadoresDesdeImportacion(resultado.indicadores, resultado.mediciones);
    setVistaPrevia({ indicadores: indicadoresImportados, resumen: calcularResumenImportacion(indicadoresImportados) });
  };

  const procesarArchivo = async (archivo) => {
    limpiarResultadoAnterior();
    setOrigen(archivo.name);

    const extension = archivo.name.split(".").pop().toLowerCase();
    if (extension === "csv") {
      setErrorGeneral(
        'Un archivo .csv no puede contener las dos hojas requeridas ("Indicadores" y "Mediciones"). Usa un archivo .xlsx o .xls, o conecta dos hojas en línea (una por hoja) más abajo.'
      );
      return;
    }
    if (!["xlsx", "xls"].includes(extension)) {
      setErrorGeneral("Formato de archivo no reconocido. Usa un archivo .xlsx o .xls.");
      return;
    }

    setCargando(true);
    try {
      const libro = await leerLibroDesdeArchivo(archivo);
      procesarResultadoValidacion(validarLibro(libro));
    } catch (error) {
      setErrorGeneral("No se pudo leer el archivo. Verifica que sea un Excel válido y no esté dañado.");
    } finally {
      setCargando(false);
    }
  };

  const actualizarDesdeHojaEnLinea = async () => {
    limpiarResultadoAnterior();
    if (!urlIndicadores.trim() || !urlMediciones.trim()) {
      setErrorGeneral('Indica la URL pública de la hoja "Indicadores" y la de la hoja "Mediciones" (cada una publicada como CSV).');
      return;
    }
    setOrigen("Hoja en línea");
    setCargando(true);
    try {
      const [textoIndicadores, textoMediciones] = await Promise.all([
        obtenerTextoCSV(urlIndicadores.trim()),
        obtenerTextoCSV(urlMediciones.trim()),
      ]);
      procesarResultadoValidacion(validarLibro(construirLibroDesdeCSV(textoIndicadores, textoMediciones)));
    } catch (error) {
      setErrorGeneral(
        "No se pudo obtener la hoja en línea. Verifica que ambas URL sean públicas (publicadas como CSV) y que el origen permita solicitudes CORS; algunos sitios bloquean este acceso desde el navegador."
      );
    } finally {
      setCargando(false);
    }
  };

  const manejarSeleccionArchivo = (evento) => {
    const archivo = evento.target.files?.[0];
    if (archivo) procesarArchivo(archivo);
    evento.target.value = "";
  };

  const manejarSoltar = (evento) => {
    evento.preventDefault();
    setArrastrando(false);
    const archivo = evento.dataTransfer.files?.[0];
    if (archivo) procesarArchivo(archivo);
  };

  const confirmarImportacion = async (modo) => {
    if (!vistaPrevia) return;
    setGuardando(true);
    setErrorGeneral("");
    try {
      await onImportar(aplicarImportacion(indicadoresActuales, vistaPrevia.indicadores, modo));
      setVistaPrevia(null);
      setImportado(true);
    } catch (err) {
      setErrorGeneral(`No se pudo guardar en el servidor: ${err.message}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Carga y conexión de datos</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-gray-500">
            Sesión: <span className="font-medium text-gray-800">{sesion}</span>
          </span>
          <button onClick={onCerrarSesion} className="inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-900 font-medium">
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Cerrar sesión
          </button>
          <button
            onClick={descargarPlantillaExcel}
            className="inline-flex items-center gap-2 text-sm font-medium border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-600"
          >
            <Download className="w-4 h-4" aria-hidden="true" />
            Descargar plantilla Excel
          </button>
        </div>
      </div>

      {importado && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-md px-4 py-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" />
          Datos importados correctamente.
          <button onClick={onIrAMenu} className="underline underline-offset-2 font-medium">
            Ver indicadores
          </button>
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={manejarSoltar}
        className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
          arrastrando ? "border-teal-500 bg-teal-50" : "border-gray-300 bg-white"
        }`}
      >
        <UploadCloud className="w-10 h-10 text-gray-300 mx-auto mb-3" aria-hidden="true" />
        <p className="text-sm text-gray-600 mb-3">Arrastra aquí tu archivo, o</p>
        <button
          onClick={() => inputArchivoRef.current?.click()}
          className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white font-medium px-4 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          <Upload className="w-4 h-4" aria-hidden="true" />
          Seleccionar archivo
        </button>
        <input
          ref={inputArchivoRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={manejarSeleccionArchivo}
          className="hidden"
        />
        <p className="text-xs text-gray-400 mt-3">Formatos aceptados: .xlsx, .xls (hojas "Indicadores" y "Mediciones"). El .csv no admite dos hojas.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-gray-500" aria-hidden="true" />
          <h3 className="font-semibold text-gray-800 text-sm">Conectar una hoja en línea</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Pega la URL pública (publicada como CSV) de cada hoja de tu Google Sheet — por ejemplo la que termina en{" "}
          <code className="bg-gray-100 px-1 rounded">.../pub?output=csv</code> — o la de un archivo CSV accesible
          públicamente. La hoja debe ser pública; los orígenes que no habiliten CORS para el navegador no podrán
          conectarse desde aquí.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600">
            URL de la hoja "Indicadores" (CSV)
            <input
              type="url"
              value={urlIndicadores}
              onChange={(e) => setUrlIndicadores(e.target.value)}
              placeholder="https://docs.google.com/.../pub?output=csv"
              className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600"
            />
          </label>
          <label className="text-xs text-gray-600">
            URL de la hoja "Mediciones" (CSV)
            <input
              type="url"
              value={urlMediciones}
              onChange={(e) => setUrlMediciones(e.target.value)}
              placeholder="https://docs.google.com/.../pub?output=csv"
              className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600"
            />
          </label>
        </div>
        <button
          onClick={actualizarDesdeHojaEnLinea}
          disabled={cargando}
          className="mt-4 inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
        >
          <RefreshCw className={`w-4 h-4 ${cargando ? "animate-spin" : ""}`} aria-hidden="true" />
          Actualizar ahora
        </button>
      </div>

      {cargando && (
        <p className="flex items-center gap-2 text-sm text-teal-700">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Procesando {origen ? `"${origen}"` : "el archivo"}…
        </p>
      )}

      {!cargando && origen && !errorGeneral && !errores.length && !vistaPrevia && !importado && (
        <p className="text-xs text-gray-500">Última fuente procesada: {origen}</p>
      )}

      {errorGeneral && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorGeneral}</span>
        </div>
      )}

      {errores.length > 0 && <ReporteErrores errores={errores} />}

      {vistaPrevia && (
        <VistaPreviaImportacion
          resumen={vistaPrevia.resumen}
          guardando={guardando}
          onReemplazar={() => confirmarImportacion("reemplazar")}
          onAgregar={() => confirmarImportacion("agregar")}
          onCancelar={() => {
            setVistaPrevia(null);
            setOrigen("");
          }}
        />
      )}

      <button
        onClick={onIrAMenu}
        className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        Volver al menú de indicadores
      </button>
    </div>
  );
}

function ReporteErrores({ errores }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-center gap-2 text-red-800 font-semibold text-sm mb-2">
        <CircleAlert className="w-4 h-4" aria-hidden="true" />
        No se importó ningún dato: se encontraron {errores.length} error{errores.length === 1 ? "" : "es"}
      </div>
      <div className="overflow-x-auto max-h-64 overflow-y-auto border border-red-200 rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-red-100 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium text-red-900">Hoja</th>
              <th className="text-left px-2 py-1.5 font-medium text-red-900">Fila</th>
              <th className="text-left px-2 py-1.5 font-medium text-red-900">Columna</th>
              <th className="text-left px-2 py-1.5 font-medium text-red-900">Motivo</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {errores.map((error, idx) => (
              <tr key={idx} className="border-t border-red-100">
                <td className="px-2 py-1.5 text-gray-700">{error.hoja}</td>
                <td className="px-2 py-1.5 text-gray-700">{error.fila}</td>
                <td className="px-2 py-1.5 text-gray-700">{error.columna}</td>
                <td className="px-2 py-1.5 text-red-700">{error.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VistaPreviaImportacion({ resumen, guardando, onReemplazar, onAgregar, onCancelar }) {
  return (
    <div className="bg-white border border-teal-200 rounded-lg p-5 space-y-4">
      <div className="flex items-center gap-2 text-teal-800 font-semibold text-sm">
        <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
        Archivo válido: vista previa de la importación
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="bg-gray-50 rounded-md p-3">
          <p className="text-xs text-gray-500">Indicadores</p>
          <p className="font-bold text-gray-900 text-lg">{resumen.numIndicadores}</p>
        </div>
        <div className="bg-gray-50 rounded-md p-3">
          <p className="text-xs text-gray-500">Mediciones</p>
          <p className="font-bold text-gray-900 text-lg">{resumen.numMediciones}</p>
        </div>
        <div className="bg-gray-50 rounded-md p-3">
          <p className="text-xs text-gray-500">Rango de periodos</p>
          <p className="font-bold text-gray-900 text-sm">
            {resumen.periodoInicio ? `${formatearPeriodo(resumen.periodoInicio)} – ${formatearPeriodo(resumen.periodoFin)}` : "N/D"}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          onClick={onReemplazar}
          disabled={guardando}
          className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-medium px-4 py-2 rounded-md text-sm"
        >
          Reemplazar todo
        </button>
        <button
          onClick={onAgregar}
          disabled={guardando}
          className="border border-teal-700 text-teal-700 hover:bg-teal-50 disabled:opacity-60 font-medium px-4 py-2 rounded-md text-sm"
        >
          Agregar / actualizar
        </button>
        <button onClick={onCancelar} disabled={guardando} className="text-gray-500 hover:text-gray-700 disabled:opacity-60 font-medium px-4 py-2 rounded-md text-sm">
          Cancelar
        </button>
        {guardando && (
          <span className="inline-flex items-center gap-2 text-sm text-teal-700">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Guardando en el servidor...
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE: PantallaLogin
// ============================================================================

function PantallaLogin({ onIniciarSesion, mensaje }) {
  const [usuario, setUsuario] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const manejarEnvio = async (evento) => {
    evento.preventDefault();
    setError("");
    setEnviando(true);
    try {
      const errorServidor = await onIniciarSesion(usuario.trim(), contrasena);
      if (errorServidor) setError(errorServidor);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto bg-white border border-gray-200 rounded-lg p-6 space-y-4">
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-teal-700" aria-hidden="true" />
        <h2 className="text-lg font-bold text-gray-900">Acceso restringido</h2>
      </div>
      <p className="text-sm text-gray-600">{mensaje}</p>
      <form onSubmit={manejarEnvio} className="space-y-3">
        <label className="block text-xs text-gray-600">
          Usuario
          <input
            type="text"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600"
            required
          />
        </label>
        <label className="block text-xs text-gray-600">
          Contraseña
          <input
            type="password"
            value={contrasena}
            onChange={(e) => setContrasena(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600"
            required
          />
        </label>
        {error && (
          <p className="flex items-center gap-2 text-sm text-red-700">
            <CircleAlert className="w-4 h-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={enviando}
          className="w-full inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-medium px-4 py-2 rounded-md text-sm"
        >
          {enviando ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <LogIn className="w-4 h-4" aria-hidden="true" />
          )}
          {enviando ? "Verificando..." : "Iniciar sesión"}
        </button>
      </form>
    </div>
  );
}

// ============================================================================
// COMPONENTE: AdminUsuarios
// ============================================================================

function AdminUsuarios({ sesion, onCerrarSesion, onIrAMenu }) {
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [nuevoUsuario, setNuevoUsuario] = useState("");
  const [nuevaContrasena, setNuevaContrasena] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargarUsuarios = () => {
    setCargando(true);
    setErrorCarga("");
    apiFetch("/usuarios")
      .then((datos) => setUsuarios(datos))
      .catch((err) => setErrorCarga(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargarUsuarios, []);

  const manejarAgregar = async (evento) => {
    evento.preventDefault();
    setError("");
    const usuarioLimpio = nuevoUsuario.trim();
    if (!usuarioLimpio || !nuevaContrasena) {
      setError("Usuario y contraseña son obligatorios.");
      return;
    }
    setEnviando(true);
    try {
      await apiFetch("/usuarios", { method: "POST", body: JSON.stringify({ usuario: usuarioLimpio, contrasena: nuevaContrasena }) });
      setNuevoUsuario("");
      setNuevaContrasena("");
      cargarUsuarios();
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const manejarEliminar = async (usuario) => {
    setError("");
    try {
      await apiFetch(`/usuarios/${encodeURIComponent(usuario)}`, { method: "DELETE" });
      if (usuario === sesion) {
        onCerrarSesion();
        return;
      }
      cargarUsuarios();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">Administrar usuarios</h2>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>
            Sesión: <span className="font-medium text-gray-800">{sesion}</span>
          </span>
          <button onClick={onCerrarSesion} className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 font-medium">
            <LogOut className="w-4 h-4" aria-hidden="true" />
            Cerrar sesión
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
        Las contraseñas se guardan cifradas (nunca en texto plano) y los usuarios quedan almacenados de forma
        permanente en el servidor.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-3">Usuarios con acceso a carga de datos</h3>
        {cargando ? (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Cargando usuarios...
          </p>
        ) : errorCarga ? (
          <p className="text-sm text-red-700">{errorCarga}</p>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Usuario</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600"></th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => (
                  <tr key={u.usuario} className="border-t border-gray-100">
                    <td className="px-3 py-2 text-gray-800">{u.usuario}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => manejarEliminar(u.usuario)} className="text-red-600 hover:text-red-800 text-xs font-medium">
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form onSubmit={manejarAgregar} className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
        <h3 className="font-semibold text-gray-800 text-sm">Dar de alta un nuevo usuario</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600">
            Usuario
            <input
              type="text"
              value={nuevoUsuario}
              onChange={(e) => setNuevoUsuario(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600"
            />
          </label>
          <label className="text-xs text-gray-600">
            Contraseña (mínimo 6 caracteres)
            <input
              type="password"
              value={nuevaContrasena}
              onChange={(e) => setNuevaContrasena(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-md text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600"
            />
          </label>
        </div>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <button
          type="submit"
          disabled={enviando}
          className="inline-flex items-center gap-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white font-medium px-4 py-2 rounded-md text-sm"
        >
          {enviando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <UserPlus className="w-4 h-4" aria-hidden="true" />}
          Agregar usuario
        </button>
      </form>

      <button onClick={onIrAMenu} className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        Volver al menú de indicadores
      </button>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL: TableroFIPROTUY
// ============================================================================

export default function TableroFIPROTUY() {
  const [vista, setVista] = useState("inicio");
  const [indicadorActivoId, setIndicadorActivoId] = useState(null);
  const [indicadores, setIndicadores] = useState([]);
  const [esDemostracion, setEsDemostracion] = useState(true);
  const [sesion, setSesion] = useState(null);
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");

  const indicadorActivo = indicadores.find((i) => i.id === indicadorActivoId) || null;

  const cargarIndicadores = () => {
    setCargandoInicial(true);
    setErrorCarga("");
    apiFetch("/indicadores")
      .then((datos) => {
        setIndicadores(datos.indicadores);
        setEsDemostracion(datos.esDemostracion);
      })
      .catch((err) => setErrorCarga(err.message))
      .finally(() => setCargandoInicial(false));
  };

  // Carga inicial: trae los indicadores persistidos en el servidor y comprueba si ya hay
  // una sesión activa (la cookie httpOnly del backend sobrevive a recargar la página).
  useEffect(() => {
    cargarIndicadores();
    apiFetch("/auth/me")
      .then((datos) => setSesion(datos.usuario))
      .catch(() => {});
  }, []);

  const iniciarSesion = async (usuario, contrasena) => {
    try {
      const datos = await apiFetch("/auth/login", { method: "POST", body: JSON.stringify({ usuario, contrasena }) });
      setSesion(datos.usuario);
      return null;
    } catch (err) {
      return err.message;
    }
  };

  const cerrarSesion = async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } finally {
      setSesion(null);
    }
  };

  const irAVista = (destino) => {
    setVista(destino);
  };

  const seleccionarIndicador = (id) => {
    setIndicadorActivoId(id);
    setVista("detalle");
  };

  const volverAlMenu = () => {
    setIndicadorActivoId(null);
    setVista("menu");
  };

  /** Persiste el arreglo final de indicadores en el servidor (requiere sesión iniciada). */
  const guardarIndicadores = async (indicadoresResultantes) => {
    const datos = await apiFetch("/indicadores", {
      method: "PUT",
      body: JSON.stringify({ indicadores: indicadoresResultantes, esDemostracion: false }),
    });
    setIndicadores(datos.indicadores);
    setEsDemostracion(datos.esDemostracion);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      <Header />
      <Nav vista={vista} setVista={irAVista} />
      <main className="max-w-7xl mx-auto px-4 py-6">
        {cargandoInicial ? (
          <div className="flex items-center justify-center gap-2 py-24 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            Cargando indicadores...
          </div>
        ) : errorCarga ? (
          <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6 text-center space-y-3">
            <CircleAlert className="w-8 h-8 text-red-600 mx-auto" aria-hidden="true" />
            <p className="text-sm text-red-700">No se pudo conectar con el servidor: {errorCarga}</p>
            <button
              onClick={cargarIndicadores}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-md text-sm"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Reintentar
            </button>
          </div>
        ) : (
          <>
            {vista === "inicio" && (
              <Inicio
                indicadores={indicadores}
                esDemostracion={esDemostracion}
                sesion={sesion}
                onIrAMenu={() => irAVista("menu")}
                onIrACarga={() => irAVista("datos")}
                onBorrarDemo={() => guardarIndicadores([])}
              />
            )}
            {vista === "menu" && (
              <MenuIndicadores
                indicadores={indicadores}
                esDemostracion={esDemostracion}
                onSeleccionar={seleccionarIndicador}
                onIrACarga={() => irAVista("datos")}
              />
            )}
            {vista === "detalle" &&
              (indicadorActivo ? (
                <DetalleIndicador indicador={indicadorActivo} onVolver={volverAlMenu} />
              ) : (
                <div className="text-sm text-gray-500">
                  El indicador seleccionado ya no existe.{" "}
                  <button onClick={volverAlMenu} className="text-teal-700 underline">
                    Volver al menú
                  </button>
                </div>
              ))}
            {vista === "datos" &&
              (sesion ? (
                <CargaDatos
                  indicadoresActuales={indicadores}
                  onImportar={guardarIndicadores}
                  onIrAMenu={() => irAVista("menu")}
                  sesion={sesion}
                  onCerrarSesion={cerrarSesion}
                />
              ) : (
                <PantallaLogin onIniciarSesion={iniciarSesion} mensaje="Inicia sesión para cargar datos al tablero." />
              ))}
            {vista === "usuarios" &&
              (sesion ? (
                <AdminUsuarios sesion={sesion} onCerrarSesion={cerrarSesion} onIrAMenu={() => irAVista("menu")} />
              ) : (
                <PantallaLogin onIniciarSesion={iniciarSesion} mensaje="Inicia sesión para administrar usuarios." />
              ))}
          </>
        )}
      </main>
      <footer className="max-w-7xl mx-auto px-4 py-6 text-xs text-gray-400 space-y-1">
        <p>{NOMBRE_INSTITUCION} · {NOMBRE_INSTITUCION_LARGO}</p>
        <p>Elaborado por Grupo Empresarial Estrategia – Gemes</p>
      </footer>
    </div>
  );
}

import ReactDOM from "react-dom/client";
ReactDOM.createRoot(document.getElementById("root")).render(<TableroFIPROTUY />);
